import { useGet } from "@albertine/api-client";
import { useAgentTeams } from "../../common/hooks/useAgentTeams";
import type { Agent } from "../../common/types/Agent";
import {
    type FilterByAssignee,
    getActiveAssigneeSelections,
    getInactiveAssigneeSelections,
    teamFilterItems,
    unassignedAgent,
} from "../../common/utils/agents.util";
import { onlyUnique, partition } from "../../common/utils/array.util";
import {
    conversationAssigneeCounts,
    getTotalUnreadMessages,
} from "../../utils/conversation.util";
import { useMessagesUrlParams } from "../../utils/customHook.util";
import MessageListItem from "./MessageListItem";
import "./MessagesList.css";
import { Flex } from "../Common/Flex";
import ListFilterGroup from "../Common/ListFilterGroup";
import Tag from "../Common/Tag";

const MessagesList = function Menu() {
    const { data: allAgentsData } = useGet(
        "getAllAgents",
        {},
        {
            staleTime: 5 * 60 * 1000,
        },
    );
    const { urlState, setUrlState } = useMessagesUrlParams();
    const selectedAgentIds = urlState.assignedToAgentIds;
    const selectedTeamIds = urlState.teamIds ?? [];
    const teams = useAgentTeams();
    const hasActiveFilter =
        (selectedAgentIds?.length ?? 0) > 0 || selectedTeamIds.length > 0;

    const { data: recentConversations } = useGet(
        "recentConversations",
        {
            query: {
                ...(selectedAgentIds?.length
                    ? { assignedToAgentIds: selectedAgentIds }
                    : {}),
                ...(selectedTeamIds.length ? { teamIds: selectedTeamIds } : {}),
            },
        },
        {
            refetchInterval: 5000,
        },
    );
    const conversations = recentConversations?.items ?? [];

    const assignees: Agent[] = (allAgentsData?.results ?? []) as Agent[];
    const assigneeCounts = conversationAssigneeCounts(conversations);

    const activePlmFilterItems: {
        data: FilterByAssignee;
        selected: boolean;
        count: number;
    }[] = [
        {
            data: unassignedAgent,
            selected: (selectedAgentIds ?? []).includes(unassignedAgent.id),
            count: assigneeCounts[unassignedAgent.id] ?? 0,
        },
        ...assignees
            .filter((agent) => !agent.disabled)
            .map((agent) => ({
                data: agent,
                selected: (selectedAgentIds ?? []).includes(agent.id),
                count: assigneeCounts[agent.id] ?? 0,
            })),
    ];

    const inactivePlmFilterItems = assignees
        .filter((agent) => agent.disabled)
        .map((agent) => ({
            data: agent,
            selected: (selectedAgentIds ?? []).includes(agent.id),
            count: assigneeCounts[agent.id] ?? 0,
        }));

    const [conversationsWithUnreadMessages, readConversations] = partition(
        conversations,
        (conversation) =>
            conversation.conversation.unreadMessagesCountByAgent > 0 ||
            conversation.conversation.agentUnseenReactionsCount > 0,
    );

    const isEmpty = conversations.length === 0;

    const updateAssignedToFilter = (newAgentIds: string[]) => {
        setUrlState({ assignedTo: newAgentIds });
    };

    const plmFilter = (
        <ListFilterGroup<FilterByAssignee>
            title="Assigned agents"
            showAllFiltersWithoutCount
            filters={[
                ...(teams.length > 0
                    ? [
                          {
                              title: "Teams",
                              items: teamFilterItems(teams, selectedTeamIds),
                              onChange: (selected: FilterByAssignee[]) => {
                                  setUrlState({
                                      teamIds: selected.map((item) => item.id),
                                  });
                              },
                          },
                      ]
                    : []),
                {
                    title: "Active agents",
                    items: activePlmFilterItems,
                    onChange: (newAgentList) => {
                        updateAssignedToFilter(
                            [
                                ...getInactiveAssigneeSelections(
                                    assignees,
                                    selectedAgentIds ?? [],
                                ),
                                ...newAgentList.map((it) => it.id),
                            ].filter(onlyUnique),
                        );
                    },
                },
                ...(inactivePlmFilterItems.length > 0
                    ? [
                          {
                              title: "Inactive agents",
                              items: inactivePlmFilterItems,
                              onChange: (newAgentList: FilterByAssignee[]) => {
                                  updateAssignedToFilter(
                                      [
                                          ...getActiveAssigneeSelections(
                                              assignees,
                                              selectedAgentIds ?? [],
                                          ),
                                          ...((selectedAgentIds ?? []).includes(
                                              unassignedAgent.id,
                                          )
                                              ? [unassignedAgent.id]
                                              : []),
                                          ...newAgentList.map((it) => it.id),
                                      ].filter(onlyUnique),
                                  );
                              },
                          },
                      ]
                    : []),
            ]}
            position="bottom-right"
        />
    );

    return (
        <div className="tab">
            <Flex.Row
                className="messages-tab__header"
                gap="medium"
                justifyContent="space-between"
            >
                <Flex.Row gap="medium">
                    <h3>New messages</h3>
                    <Tag.Medium.N90>
                        {getTotalUnreadMessages(
                            conversations.map(
                                (conversation) => conversation.conversation,
                            ),
                        )}
                    </Tag.Medium.N90>
                </Flex.Row>
                {plmFilter}
            </Flex.Row>

            {isEmpty && hasActiveFilter ? (
                <div className="px-5 py-6 text-(--a-ds-font-color-secondary)">
                    No messages match this filter.
                </div>
            ) : null}

            {conversationsWithUnreadMessages.map((conversation) => (
                <MessageListItem
                    key={conversation.conversation.id}
                    conversation={conversation.conversation}
                    member={conversation.member}
                    requests={conversation.requests}
                />
            ))}

            <Flex.Row className="messages-tab__header" gap="medium">
                <h3>Read messages</h3>
                <Tag.Small.Light.N90>within 48 hours</Tag.Small.Light.N90>
            </Flex.Row>
            {readConversations.map((conversation) => (
                <MessageListItem
                    key={conversation.conversation.id}
                    conversation={conversation.conversation}
                    member={conversation.member}
                    requests={conversation.requests}
                />
            ))}
        </div>
    );
};

export default MessagesList;
